import React from "react";
import { Phone, Heart, ShieldCheck, Wrench, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import SectionLabel from "@/components/SectionLabel";
import EmergencyBanner from "@/components/EmergencyBanner";
import { PRIMARY_PHONE, SITE } from "@/lib/site";

const ABOUT_IMG = "https://images.unsplash.com/photo-1742069029207-0aacf8fa4401?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";

const VALUES = [
  { icon: Heart, title: "Locally owned", body: "We live and work in Victoria. When you call, you're talking to your neighbours — not a call centre." },
  { icon: ShieldCheck, title: "Treated like family", body: "Friendly drivers, modern equipment and a duty of care for every customer and every vehicle." },
  { icon: Wrench, title: "No job too big or small", body: "From a flat tyre on the M1 to relocating a heavy box truck across the state." },
];

const About = () => (
  <div data-testid="about-page">
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-12 grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-6">
          <SectionLabel>About us</SectionLabel>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.05]">
            Built on cheap prices, fast trucks and trust earned the hard way.
          </h1>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">
            Vic Wide Cheap Towing Service is a locally owned and operated towing company servicing all of Victoria from our base in Sale and additional location in Albion.
            We started this business with a simple goal: to be the cheapest, fastest and most reliable choice when something goes wrong on the road.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
            Years later, we still believe in the same thing — clear pricing, honest service, and a real human on the other end of the phone, day or night.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${PRIMARY_PHONE.tel}`}
              data-testid="about-call-button"
              className="inline-flex items-center gap-3 rounded-full bg-[var(--color-secondary)] px-6 py-3.5 font-display font-extrabold text-white hover:-translate-y-0.5 transition-all"
            >
              <Phone className="h-4 w-4" />
              Call {PRIMARY_PHONE.number}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-6 py-3.5 font-display font-bold text-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
            >
              Get a Free Quote
            </Link>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
            <img src={ABOUT_IMG} alt="Modern tow truck ready for dispatch" className="w-full h-[420px] object-cover" />
            <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 backdrop-blur p-5 shadow-md">
              <div className="grid grid-cols-3 gap-4 text-center">
                {[
                  { v: "24/7", l: "Always on" },
                  { v: `${SITE.rating}★`, l: `${SITE.reviewsCount} reviews` },
                  { v: "$70+", l: "Fair pricing" },
                ].map((s) => (
                  <div key={s.l}>
                    <p className="font-num font-extrabold text-2xl text-slate-900">{s.v}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-20 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionLabel color="amber">What we believe</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 max-w-2xl">
          Every job is a chance to earn another customer for life.
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {VALUES.map((v) => (
            <div
              key={v.title}
              data-testid={`about-value-${v.title.replace(/\s+/g, "-").toLowerCase()}`}
              className="rounded-2xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-md transition-all"
            >
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-blue-50 text-[var(--color-primary)]">
                <v.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-xl font-extrabold text-slate-900">
                {v.title}
              </h3>
              <p className="mt-2 text-slate-600 leading-relaxed text-sm">{v.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-white">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 grid gap-10 md:grid-cols-2">
        {SITE.locations.map((loc) => (
          <div key={loc.address} className="rounded-2xl border border-slate-200 p-7 bg-slate-50">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[var(--color-secondary)]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                Location
              </p>
            </div>
            <h3 className="mt-3 font-display text-2xl font-extrabold text-slate-900">{loc.city}</h3>
            <p className="mt-1 text-slate-600">{loc.address}</p>
            <p className="text-slate-500 text-sm">{loc.region}</p>
          </div>
        ))}
      </div>
    </section>

    <EmergencyBanner />
  </div>
);

export default About;
